import { useEffect, useMemo, useState } from "react";
import { listAuditEvents, type AuditEvent } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * /audit-log — Plan §21 Phase 5 first Enterprise deliverable.
 *
 * Shows the signed-in user's recent mutating actions, newest first.
 * Filters: action-prefix chip strip (e.g. "billing.", "heal.",
 * "integration."). Admin users get a free-text prefix box that hits
 * the cross-user view.
 *
 * v1 scope: 100 events per page, no pagination, no CSV export.
 * Compliance-export PDF/CSV is a v2 follow-up.
 */
export function AuditLogPage(): JSX.Element {
  const { user } = useAuth();
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [adminPrefix, setAdminPrefix] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.plan === "admin";

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const list = await listAuditEvents(
        isAdmin && adminPrefix.trim()
          ? { prefix: adminPrefix.trim(), limit: 200 }
          : { limit: 200 },
      );
      setEvents(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the chip strip from the actions actually present in the
  // returned events — saves us from showing prefixes that never appear
  // for this user.
  const prefixes = useMemo(() => {
    if (!events) return [];
    const set = new Set<string>();
    for (const e of events) {
      const i = e.action.indexOf(".");
      if (i > 0) set.add(e.action.slice(0, i));
    }
    return [...set].sort();
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [];
    if (!filter) return events;
    return events.filter((e) => e.action.startsWith(filter + "."));
  }, [events, filter]);

  if (!user) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-8 text-center text-slate-300">
        <p>Sign in to view your audit log.</p>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <p className="mt-1 text-sm text-slate-400">
          Every mutating action on your account, newest first. Append-only
          — entries can't be edited or deleted. Foundation for SOC 2 +
          enterprise compliance.
        </p>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
          {error}
        </div>
      )}

      {isAdmin && (
        <div className="mb-4 rounded-md border border-amber-700/60 bg-amber-900/20 p-3">
          <label className="block text-xs font-medium uppercase tracking-wider text-amber-300">
            Admin: filter by action prefix (cross-user)
          </label>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              value={adminPrefix}
              onChange={(e) => setAdminPrefix(e.target.value)}
              placeholder='e.g. "billing." or "heal."'
              className="flex-1 rounded-md border border-amber-700/40 bg-slate-950 px-3 py-2 text-sm text-amber-100"
            />
            <button
              type="button"
              onClick={() => void load()}
              disabled={busy}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-60"
            >
              {busy ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      )}

      {prefixes.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Filter:</span>
          <button
            type="button"
            onClick={() => setFilter("")}
            className={`rounded-full px-2 py-0.5 ${
              filter === ""
                ? "bg-indigo-600 text-white"
                : "border border-slate-700 text-slate-300 hover:bg-slate-800"
            }`}
          >
            All
          </button>
          {prefixes.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setFilter(p)}
              className={`rounded-full px-2 py-0.5 ${
                filter === p
                  ? "bg-indigo-600 text-white"
                  : "border border-slate-700 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {p}
            </button>
          ))}
          <span className="ml-auto text-slate-500">
            {filtered.length} of {events?.length ?? 0} events
          </span>
        </div>
      )}

      {events === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-sm text-slate-400">
          {filter
            ? `No "${filter}.*" events on this account yet.`
            : "No audit events on this account yet. Take an action — like uploading a heal, configuring a notification, or starting a run — and refresh."}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((e) => (
            <li
              key={e.id}
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <code className="font-mono text-indigo-300">{e.action}</code>
                <span className="text-xs text-slate-500">
                  {new Date(e.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-400">
                {e.resourceType && (
                  <span>
                    {e.resourceType}
                    {e.resourceId && (
                      <>
                        {" "}
                        <code className="font-mono text-slate-300">
                          {e.resourceId}
                        </code>
                      </>
                    )}
                  </span>
                )}
                {e.ip && <span>IP: {e.ip}</span>}
                {e.metadata?.status !== undefined && (
                  <span>HTTP {String(e.metadata.status)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
